/**
 * Cognito authorization-code flow with PKCE.
 *
 * Hand-rolled rather than pulled from a library, for a specific reason: the
 * whole flow is about eighty lines of standard OAuth, and every dependency in
 * the authentication path is a supply-chain risk on the one component that
 * guards the household's medical records.
 *
 * The password and the MFA challenge are handled entirely by Cognito's managed
 * login. This code never sees either, which is the point (PRD §9). Passkey
 * enrolment and passkey sign-in also happen there — see
 * docs/authentication-authorization.md.
 *
 * Tokens live in sessionStorage, not localStorage: a closed tab should not
 * leave a usable session behind on a shared device.
 */

export interface AppConfig {
  userPoolId: string;
  clientId: string;
  cognitoDomain: string;
  region: string;
  apiBase: string;
}

interface TokenSet {
  access_token: string;
  id_token: string;
  refresh_token?: string;
  expires_at: number;
}

const STORAGE_KEY = 'hsa.tokens';
const VERIFIER_KEY = 'hsa.pkce_verifier';
const RETURN_KEY = 'hsa.return_to';

let config: AppConfig | null = null;

/** Loaded at boot from /config.json, which the deploy writes from stack outputs. */
export async function loadConfig(): Promise<AppConfig> {
  if (config) return config;
  const res = await fetch('/config.json', { cache: 'no-store' });
  if (!res.ok) {
    throw new Error(
      'config.json is missing. The deploy writes it from the CloudFormation outputs — ' +
        'see infra/deploy.sh.',
    );
  }
  config = (await res.json()) as AppConfig;
  return config;
}

export function getConfig(): AppConfig {
  if (!config) throw new Error('config not loaded');
  return config;
}

// ---------------------------------------------------------------------------
// PKCE
// ---------------------------------------------------------------------------

function base64UrlEncode(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function sha256(input: string): Promise<Uint8Array> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  return new Uint8Array(digest);
}

function randomVerifier(): string {
  return base64UrlEncode(crypto.getRandomValues(new Uint8Array(64)));
}

// ---------------------------------------------------------------------------
// Flow
// ---------------------------------------------------------------------------

export async function login(returnTo: string = window.location.pathname): Promise<void> {
  const cfg = getConfig();
  const verifier = randomVerifier();
  const challenge = base64UrlEncode(await sha256(verifier));

  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(RETURN_KEY, returnTo);

  const url = new URL(`https://${cfg.cognitoDomain}/oauth2/authorize`);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('client_id', cfg.clientId);
  url.searchParams.set('redirect_uri', `${window.location.origin}/callback`);
  url.searchParams.set('scope', 'openid email profile');
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');

  window.location.assign(url.toString());
}

export async function handleCallback(code: string): Promise<string> {
  const cfg = getConfig();
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  if (!verifier) throw new Error('no PKCE verifier in this session — start the sign-in again');

  const res = await fetch(`https://${cfg.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: cfg.clientId,
      code,
      redirect_uri: `${window.location.origin}/callback`,
      code_verifier: verifier,
    }),
  });

  if (!res.ok) throw new Error(`token exchange failed: ${res.status} ${await res.text()}`);

  const tokens = (await res.json()) as {
    access_token: string;
    id_token: string;
    refresh_token?: string;
    expires_in: number;
  };
  store({
    access_token: tokens.access_token,
    id_token: tokens.id_token,
    refresh_token: tokens.refresh_token,
    expires_at: Date.now() + tokens.expires_in * 1000,
  });

  sessionStorage.removeItem(VERIFIER_KEY);
  const returnTo = sessionStorage.getItem(RETURN_KEY) ?? '/';
  sessionStorage.removeItem(RETURN_KEY);
  return returnTo;
}

export function logout(): void {
  const cfg = getConfig();
  sessionStorage.removeItem(STORAGE_KEY);
  const url = new URL(`https://${cfg.cognitoDomain}/logout`);
  url.searchParams.set('client_id', cfg.clientId);
  url.searchParams.set('logout_uri', window.location.origin);
  window.location.assign(url.toString());
}

/** Where the user manages passkeys and their authenticator app. */
export function accountSettingsUrl(): string {
  return `https://${getConfig().cognitoDomain}/`;
}

function store(tokens: TokenSet): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
}

function read(): TokenSet | null {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as TokenSet;
  } catch {
    return null;
  }
}

/**
 * A valid access token, refreshing if it is close to expiry.
 *
 * The 60-second skew matters more than it looks: capture happens on phones on
 * bad networks, and a token that expires mid-upload would drop a receipt the
 * user believes they filed.
 */
export async function getAccessToken(): Promise<string | null> {
  const tokens = read();
  if (!tokens) return null;
  if (tokens.expires_at - 60_000 > Date.now()) return tokens.access_token;
  if (!tokens.refresh_token) return null;

  const cfg = getConfig();
  const res = await fetch(`https://${cfg.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: cfg.clientId,
      refresh_token: tokens.refresh_token,
    }),
  });
  if (!res.ok) {
    sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }

  const refreshed = (await res.json()) as {
    access_token: string;
    id_token: string;
    expires_in: number;
  };
  store({
    access_token: refreshed.access_token,
    id_token: refreshed.id_token,
    // Cognito does not return a new refresh token on this grant; the original
    // stays valid for its full 30 days.
    refresh_token: tokens.refresh_token,
    expires_at: Date.now() + refreshed.expires_in * 1000,
  });
  return refreshed.access_token;
}

export function isSignedIn(): boolean {
  return read() !== null;
}

export interface IdClaims {
  sub: string;
  email?: string;
  'cognito:username'?: string;
  'cognito:groups'?: string[];
}

/**
 * Claims from the ID token, for display only. Authorization decisions are made
 * server-side against the access token (TR-39) — anything read here is a hint
 * about what to render, never a permission.
 */
export function claims(): IdClaims | null {
  const tokens = read();
  if (!tokens) return null;
  try {
    const payload = tokens.id_token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as IdClaims;
  } catch {
    return null;
  }
}
